/* Family Combo Builder: isolated, vanilla-JS component for Shopify Dawn. */
(() => {
  'use strict';

  const DEFAULT_MEMBER_TYPES = {
    dad: { label: 'Dad', singular: 'Dad', plural: 'Dads', showOptionLabels: true },
    mom: { label: 'Mom', singular: 'Mom', plural: 'Moms' },
    son: { label: 'Son', singular: 'Son', plural: 'Sons' },
    daughter: { label: 'Daughter', singular: 'Daughter', plural: 'Daughters' }
  };

  class FamilyComboBuilder {
    constructor(root) {
      this.root = root;
      this.products = {};
      this.members = [];
      this.memberId = 0;
      this.currency = root.dataset.currency || 'INR';
      this.parentHandle = root.dataset.productHandle;
      this.comboConfig = this.parseComboConfig(root.dataset.comboConfig);
      this.memberTypes = this.comboConfig ? this.comboConfig.members.reduce((types, member) => {
        types[member.key] = { label: member.label, singular: member.singular || member.label, plural: member.plural || member.label, showOptionLabels: Boolean(member.show_option_labels) };
        return types;
      }, {}) : DEFAULT_MEMBER_TYPES;
      this.memberDefinitions = this.comboConfig ? this.comboConfig.members : Object.keys(DEFAULT_MEMBER_TYPES).map((key) => ({ key, product_handle: root.dataset[`${key}Handle`] }));
      // The no-configuration path derives children from the visible product handle.
      // Example: blue-family-combo-dad, blue-family-combo-mom, and so on.
      this.ranges = [];
      this.elements = {
        loading: root.querySelector('[data-builder-loading]'),
        content: root.querySelector('[data-builder-content]'),
        error: root.querySelector('[data-builder-error]'),
        message: root.querySelector('[data-builder-message]'),
        cards: root.querySelector('[data-member-cards]'),
        total: root.querySelector('[data-estimated-total]'),
        pieceCount: root.querySelector('[data-piece-count]'),
        addToCart: root.querySelector('[data-add-combo]')
        ,rangePicker: root.querySelector('[data-range-picker]')
        ,rangeSelect: root.querySelector('[data-range-select]')
      };
    }

    async init() {
      this.bindEvents();
      try {
        this.elements.loading.hidden = true;
        this.elements.content.hidden = false;
        if (this.ranges.length) {
          this.renderRangePicker();
        } else {
          await this.activateProducts(this.defaultHandles());
        }
      } catch (error) {
        this.showError('We could not load the family options. Please refresh and try again.');
        console.error('Family Combo Builder:', error);
      }
    }

    bindEvents() {
      this.root.addEventListener('click', (event) => {
        const addButton = event.target.closest('[data-add-member]');
        if (addButton) this.addMember(addButton.dataset.addMember);

        const removeButton = event.target.closest('[data-remove-member]');
        if (removeButton) this.removeMember(Number(removeButton.dataset.removeMember));

        if (event.target.closest('[data-add-combo]')) this.addComboToCart();
      });

      this.root.addEventListener('change', (event) => {
        const rangeSelect = event.target.closest('[data-range-select]');
        if (rangeSelect) {
          const range = this.ranges.find((item) => item.id === rangeSelect.value);
          if (range) this.activateProducts(range);
          return;
        }
        const select = event.target.closest('[data-member-option]');
        if (!select) return;
        const member = this.findMember(Number(select.dataset.memberId));
        if (!member) return;
        member.touched = true;
        if (select.dataset.singleVariant === 'true') {
          member.variant = this.products[member.type].variants.find((variant) => String(variant.id) === select.value) || null;
          this.render();
          return;
        }
        member.selections[Number(select.dataset.optionIndex)] = select.value;
        this.updateMemberVariant(member);
        this.render();
      });
    }

    async loadProducts(productHandles) {
      const types = Object.keys(productHandles);
      const loadedProducts = await Promise.all(types.map(async (type) => {
        const handle = productHandles[type];
        const response = await fetch(`${window.Shopify.routes.root}products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' }
        });
        // A missing role is intentional: for example, a Father & Son combo
        // has no -mom or -daughter product and those cards are hidden.
        if (response.status === 404) return [type, null];
        if (!response.ok) throw new Error(`Unable to load ${handle}`);
        return [type, await response.json()];
      }));
      this.products = Object.fromEntries(loadedProducts.filter(([, product]) => product));
      this.memberTypes = Object.fromEntries(Object.entries(this.memberTypes).filter(([type]) => this.products[type]));
      if (!Object.keys(this.memberTypes).length) throw new Error('No component products were found for this combo.');
    }

    async activateProducts(range) {
      this.elements.addToCart.disabled = true;
      this.showMessage('Loading selected family range…');
      await this.loadProducts(range);
      this.members = [];
      this.memberId = 0;
      Object.keys(this.memberTypes).forEach((type) => this.addMember(type, false, true));
      this.clearMessage();
      this.render();
    }

    defaultHandles() {
      if (this.comboConfig) {
        return Object.fromEntries(this.memberDefinitions.map((member) => [member.key, member.product_handle]));
      }
      return Object.fromEntries(Object.keys(DEFAULT_MEMBER_TYPES).map((role) => [role, `${this.parentHandle}-${role}`]));
    }

    parseRanges(rawConfig) {
      if (!rawConfig) return [];
      try {
        const parsed = JSON.parse(rawConfig);
        return Array.isArray(parsed) ? parsed.filter((range) =>
          range && range.id && range.label && Object.keys(this.memberTypes).every((type) => range[type])
        ) : [];
      } catch (error) {
        console.warn('Family Combo Builder: invalid family_builder_ranges metafield.', error);
        return [];
      }
    }

    parseComboConfig(rawConfig) {
      if (!rawConfig) return null;
      try {
        const config = JSON.parse(rawConfig);
        if (!config || !Array.isArray(config.members) || !config.members.length) return null;
        const valid = config.members.every((member) => member && member.key && member.label && member.product_handle);
        return valid ? config : null;
      } catch (error) {
        console.warn('Combo Builder: invalid combo_builder_config metafield.', error);
        return null;
      }
    }

    renderRangePicker() {
      this.elements.rangePicker.hidden = false;
      this.elements.rangeSelect.innerHTML = '<option value="">Select Family Range</option>' + this.ranges
        .map((range) => `<option value="${this.escapeAttribute(range.id)}">${this.escapeHtml(range.label)}</option>`)
        .join('');
      this.elements.cards.innerHTML = '';
      this.elements.addToCart.disabled = true;
    }

    addMember(type, shouldRender = true, isStarter = false) {
      const product = this.products[type];
      if (!product) return this.showMessage('Options are still loading. Please wait a moment.', true);
      const options = this.productOptions(product);
      const member = {
        id: ++this.memberId,
        type,
        selections: Array(options.length).fill(''),
        variant: null,
        isStarter,
        touched: !isStarter
      };

      // The no-option product has one auto-selected variant; it is still inventory checked.
      if (!options.length) member.variant = product.variants[0] || null;
      this.members.push(member);
      this.clearMessage();
      if (shouldRender) this.render();
    }

    removeMember(id) {
      this.members = this.members.filter((member) => member.id !== id);
      this.clearMessage();
      this.render();
    }

    findMember(id) {
      return this.members.find((member) => member.id === id);
    }

    updateMemberVariant(member) {
      const product = this.products[member.type];
      if (!this.productOptions(product).length) {
        member.variant = product.variants[0] || null;
        return;
      }
      member.variant = product.variants.find((variant) =>
        variant.options.every((value, index) => value === member.selections[index])
      ) || null;
    }

    matchingVariants(member, optionIndex, value) {
      const product = this.products[member.type];
      return product.variants.filter((variant) => variant.options.every((option, index) => {
        if (index === optionIndex) return option === value;
        return !member.selections[index] || option === member.selections[index];
      }));
    }

    isValueAvailable(member, optionIndex, value) {
      return this.matchingVariants(member, optionIndex, value).some((variant) => variant.available);
    }

    render() {
      this.elements.cards.innerHTML = Object.keys(this.memberTypes).map((type) => this.memberGroup(type)).join('');
      this.renderSummary();
      this.elements.addToCart.disabled = !this.selectedMembers().some((member) => member.variant && member.variant.available);
    }

    memberGroup(type) {
      const product = this.products[type];
      const members = this.members.filter((member) => member.type === type);
      const productTitle = this.escapeHtml(product.title);
      const cards = members.length
        ? members.map((member) => this.memberCard(member)).join('')
        : '<p class="combo-builder__group-empty">No family member added yet.</p>';
      const action = members.length ? 'Add More' : `Add ${this.memberTypes[type].plural}`;
      return `<section class="combo-builder__group combo-builder__group--${type}">
        <h3>${productTitle}</h3>
        ${cards}
        <button class="combo-builder__add-more" type="button" data-add-member="${type}"><span aria-hidden="true">+</span> ${action}</button>
      </section>`;
    }

    memberCard(member) {
      const product = this.products[member.type];
      const options = this.productOptions(product);
      const variantWarning = member.variant && !member.variant.available
        ? '<p class="combo-builder__field-error">This selection is currently sold out.</p>' : '';
      const fields = options.length
        ? options.map((option, index) => this.optionField(member, option, index)).join('')
        : this.singleVariantField(member, product);
      const staticName = '';
      return `<article class="combo-builder__card" data-member-card data-member-id="${member.id}" data-variant-id="${member.variant ? member.variant.id : ''}">
        ${staticName}<div class="combo-builder__member-options" style="--option-count: ${Math.max(options.length, 1)}">${fields}</div>${variantWarning}
        <button class="combo-builder__remove" type="button" data-remove-member="${member.id}">× Remove</button>
      </article>`;
    }

    optionField(member, option, index) {
      const values = option.values.map((value) => {
        const available = this.isValueAvailable(member, index, value);
        const selected = member.selections[index] === value;
        const price = this.optionValuePrice(member, index, value);
        const isAddOn = this.productOptions(this.products[member.type]).length > 1 && index > 0;
        const addOnPrice = isAddOn ? this.optionAddOnPrice(member, index, value) : null;
        const priceLabel = isAddOn
          ? (addOnPrice === null ? '' : ` — ${addOnPrice > 0 ? '+' : ''}${this.formatMoney(addOnPrice)}`)
          : (price === null ? '' : ` — ${this.formatMoney(price)}`);
        return `<option value="${this.escapeAttribute(value)}" ${selected ? 'selected' : ''} ${available ? '' : 'disabled'}>${this.escapeHtml(value)}${priceLabel}${available ? '' : ' — Sold out'}</option>`;
      }).join('');
      const fieldId = `combo-${this.root.dataset.sectionId}-${member.id}-${index}`;
      const title = this.memberTypes[member.type].showOptionLabels ? `<span class="combo-builder__option-title">${this.escapeHtml(option.name)}</span>` : '';
      return `<div class="field combo-builder__field">${title}
        <select class="select__select" id="${fieldId}" data-member-option data-member-id="${member.id}" data-option-index="${index}">
          <option value="" ${member.selections[index] ? '' : 'selected'}>Select Variant</option>${values}
        </select></div>`;
    }

    singleVariantField(member, product) {
      const variant = product.variants[0];
      const fieldId = `combo-${this.root.dataset.sectionId}-${member.id}-single`;
      const selected = member.touched && member.variant ? 'selected' : '';
      return `<div class="field combo-builder__field"><select class="select__select" id="${fieldId}" data-member-option data-single-variant="true" data-member-id="${member.id}">
        <option value="" ${member.touched ? '' : 'selected'}>Select Variant</option>
        <option value="${variant.id}" ${selected}>${this.escapeHtml(product.title)} — ${this.formatMoney(variant.price)}</option>
      </select></div>`;
    }

    // Show a live price beside each choice. As other options are selected,
    // this narrows to the matching Shopify variant; otherwise it shows the lowest matching price.
    optionValuePrice(member, optionIndex, value) {
      const variants = this.matchingVariants(member, optionIndex, value);
      if (!variants.length) return null;
      return Math.min(...variants.map((variant) => variant.price));
    }

    // The first option value is the base choice (for example, "Without Velcro"
    // or "Not Needed"). Other values display only their added cost.
    optionAddOnPrice(member, optionIndex, value) {
      const option = this.productOptions(this.products[member.type])[optionIndex];
      const baseValue = option && option.values[0];
      const basePrice = this.optionValuePrice(member, optionIndex, baseValue);
      const valuePrice = this.optionValuePrice(member, optionIndex, value);
      if (basePrice === null || valuePrice === null) return null;
      return valuePrice - basePrice;
    }

    // Shopify exposes a single "Title / Default Title" option for products
    // that have no customer-selectable options. It is not a real dropdown.
    productOptions(product) {
      return product.options.filter((option) => option.name.toLowerCase() !== 'title');
    }

    renderSummary() {
      const selected = this.selectedMembers();
      const purchasable = selected.filter((member) => member.variant && member.variant.available);
      const total = purchasable.reduce((sum, member) => sum + member.variant.price, 0);
      this.elements.total.textContent = purchasable.length ? this.formatMoney(total) : '—';
      this.elements.pieceCount.textContent = purchasable.length
        ? `For set of ${purchasable.length} ${purchasable.length === 1 ? 'piece' : 'pieces'} · Tax included.`
        : 'Tax included.';
    }

    selectedMembers() {
      return this.members.filter((member) => !member.isStarter || member.touched);
    }

    validate() {
      const selected = this.selectedMembers();
      if (!selected.length) return 'Select at least one family member to continue.';
      for (const member of selected) {
        const details = this.memberTypes[member.type];
        if (member.selections.some((selection) => !selection)) {
          return `Please complete the options for ${details.singular.toLowerCase()} #${this.memberPosition(member)}.`;
        }
        if (!member.variant) return `We could not find a variant for ${details.singular.toLowerCase()} #${this.memberPosition(member)}.`;
        if (!member.variant.available) return `${details.singular} #${this.memberPosition(member)} is currently sold out. Please choose another option.`;
      }
      return '';
    }

    memberPosition(member) {
      return this.members.filter((item) => item.type === member.type && item.id <= member.id).length;
    }

    async addComboToCart() {
      const validationMessage = this.validate();
      if (validationMessage) return this.showMessage(validationMessage, true);
      const button = this.elements.addToCart;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      const originalText = button.textContent;
      button.textContent = 'Adding…';
      try {
        const comboId = `${this.root.dataset.productHandle}-${Date.now()}`;
        const items = this.selectedMembers().map((member) => ({
          id: member.variant.id,
          quantity: 1,
          properties: {
            '_Family Combo': 'true',
            '_Combo ID': comboId,
            '_Combo Builder URL': window.location.pathname,
            '_Combo Name': this.root.dataset.comboTitle || 'Family Combo'
          }
        }));
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'The selected items could not be added to your cart.');
        }
        window.location.assign(`${window.Shopify.routes.root}cart`);
      } catch (error) {
        this.showMessage(error.message || 'Something went wrong. Please try again.', true);
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.textContent = originalText;
      }
    }

    showMessage(message, isError = false) {
      const element = this.elements.message;
      element.textContent = message;
      element.classList.toggle('combo-builder__notice--error', isError);
      element.hidden = false;
    }

    clearMessage() { this.elements.message.hidden = true; }
    showError(message) { this.elements.loading.hidden = true; this.elements.error.textContent = message; this.elements.error.hidden = false; }
    formatMoney(cents) { return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: this.currency }).format(cents / 100); }
    escapeHtml(value) { const node = document.createElement('div'); node.textContent = value; return node.innerHTML; }
    escapeAttribute(value) { return this.escapeHtml(value).replace(/"/g, '&quot;'); }
  }

  const initializeBuilders = () => {
    document.querySelectorAll('[data-family-combo-builder]').forEach((root) => new FamilyComboBuilder(root).init());
  };

  // A section script can be evaluated after DOMContentLoaded in the Theme Editor.
  // Run immediately in that case, otherwise wait for the initial document parse.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeBuilders, { once: true });
  } else {
    initializeBuilders();
  }
})();
